import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Checkbox, FormControlLabel, IconButton, Tooltip } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Grid from '@mui/material/Grid';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { useNavigate } from 'react-router';
import { Paper } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { AppointmentTimeFrameEnum } from '../../../Enum/AppointmentTimeFrameEnum';
import { useWidgetContext } from '../../../hooks/widgetContext';
import { configureStackedBarChart } from '../../ChartConfiguration/ChartConfiguration';
import { dayFilterList, fetchTeamAppointmentHub, MenuProps } from '../WidgetService';
import SchedulingSummary from '../../../Models/Widget/SchedulingSummary.model';
import Authorization from '../../../utils/Authorization';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import TeamDropdown from '../../Shared/TeamDropdown';
import HealthPlanMultiSelect from "../../Shared/HealthPlanMultiSelect";
import { fetchTeams } from '../../../store/careTeams.slice';
import '../Widgets.css';

const TeamAppointmentsHub = (props) => {
    const dispatch = useAppDispatch();
    const history = useNavigate();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { activeProviders } = useSelector((state: RootState) => state.provider);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [schedulingSummary, setSchedulingSummary] = useState<SchedulingSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const {
        schedulingDateFilter,
        setSchedulingDateFilter,
        schedulingFromDate,
        setSchedulingFromDate,
        schedulingToDate,
        setSchedulingToDate,
        schedulingSelectedPlans,
        setSchedulingSelectedPlans,
        schedulingSelectedProvider,
        setSchedulingSelectedProvider,
        schedulingSelectedStaff,
        setSchedulingSelectedStaff,
        schedulingSelectedTeam,
        setSchedulingSelectedTeam,
        schedulingShowInActiveProviders,
        setSchedulingShowInActiveProviders,
        schedulingShowInActiveTeams,
        setSchedulingShowInActiveTeams,
        schedulingShowInActiveStaffs,
        setSchedulingShowInActiveStaffs,
    } = useWidgetContext();
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const filteredProviders = (schedulingShowInActiveProviders ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];
    const filteredStaffs = (schedulingShowInActiveStaffs ? activeProviders : activeProviders.filter((p) => p.Role.RoleName !== 'Inactive')) || [];

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const filterList = dayFilterList();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }
        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChart(
                    'Team Appointment Hub',
                    'Appointments Count',
                    'Appointments',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'Scheduling',
                    history
                )
            );
        }

        setSchedulingSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, isGraphShow, setIsFetched, setChartOptions, schedulingSelectedPlans, history, setSchedulingSelectedTeam]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        if (schedulingDateFilter !== AppointmentTimeFrameEnum.Custom || (Boolean(schedulingFromDate) && Boolean(schedulingToDate))) {
            const changeFilter = async () => {
                const data = await fetchTeamAppointmentHub(
                    schedulingDateFilter,
                    schedulingSelectedPlans.join(","),
                    schedulingSelectedProvider,
                    schedulingSelectedTeam,
                    schedulingSelectedStaff,
                    Boolean(schedulingFromDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingFromDate).toISOString() : '',
                    Boolean(schedulingToDate) && schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? new Date(schedulingToDate).toISOString() : '',
                    schedulingShowInActiveTeams,
                    schedulingShowInActiveProviders,
                    schedulingShowInActiveStaffs,
                    false
                );
                setSchedulingSummary(data);
                setIsGraphShow(true);
            };
            setIsGraphShow(false);
            setChartOptions(
                configureStackedBarChart(
                    'Team Appointment Hub',
                    'Appointments Count',
                    'Appointments',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'Scheduling',
                    history
                )
            );
            changeFilter();
        }

        if (schedulingDateFilter !== AppointmentTimeFrameEnum.Custom && Boolean(schedulingFromDate) && Boolean(schedulingToDate)) {
            setSchedulingFromDate(null);
            setSchedulingToDate(null);
        }
    }, [
        schedulingDateFilter,
        schedulingSelectedPlans,
        schedulingSelectedProvider,
        schedulingSelectedStaff,
        schedulingFromDate,
        schedulingToDate,
        setSchedulingFromDate,
        setSchedulingToDate,
        history,
        schedulingSelectedTeam,
        schedulingShowInActiveTeams,
        schedulingShowInActiveProviders,
        schedulingShowInActiveStaffs,
    ]);

    useEffect(() => {
        if (schedulingSummary) {
            let xAxisValues = [];
            let yAxisValues = [];
            let confirmList = [];
            let cancelledList = [];
            let noCallList = [];
            let completedList = [];
            let unconfirmedList = [];
            let rescheduledList = [];
            xAxisValues.push('');

            let confirmPercentage =
                schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.ConfirmedAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
            let d = {
                y: schedulingSummary.ConfirmedAppointments,
                name: Math.round(confirmPercentage),
            };
            confirmList.push(d);
            yAxisValues.push({ name: 'Confirmed', data: confirmList, color: 'deepskyblue', showInLegend: true });

            if (Boolean(schedulingSummary.CancelledAppointments)) {
                let cancelPercentage =
                    schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.CancelledAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
                let d1 = {
                    y: schedulingSummary.CancelledAppointments,
                    name: Math.round(cancelPercentage),
                };
                cancelledList.push(d1);
                yAxisValues.push({ name: 'Cancelled', data: cancelledList, color: 'red', showInLegend: true });
            }

            if (Boolean(schedulingSummary.NoCallOrNoShowAppointments)) {
                let noCallPercentage =
                    schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.NoCallOrNoShowAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
                let d2 = {
                    y: schedulingSummary.NoCallOrNoShowAppointments,
                    name: Math.round(noCallPercentage),
                };
                noCallList.push(d2);
                yAxisValues.push({ name: 'NoCall/NoShow', data: noCallList, color: 'gray', showInLegend: true });
            }

            if (Boolean(schedulingSummary.CompletedAppointments)) {
                let completedPercentage =
                    schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.CompletedAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
                let d3 = {
                    y: schedulingSummary.CompletedAppointments,
                    name: Math.round(completedPercentage),
                };
                completedList.push(d3);
                yAxisValues.push({ name: 'Completed', data: completedList, color: 'green', showInLegend: true });
            }

            if (Boolean(schedulingSummary.UnconfirmedAppointments)) {
                let unconfirmedPercentage =
                    schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.UnconfirmedAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
                let d4 = {
                    y: schedulingSummary.UnconfirmedAppointments,
                    name: Math.round(unconfirmedPercentage),
                };
                unconfirmedList.push(d4);
                yAxisValues.push({ name: 'Unconfirmed', data: unconfirmedList, color: 'orange', showInLegend: true });
            }

            if (Boolean(schedulingSummary.RescheduledAppointments)) {
                let rescheduledPercentage =
                    schedulingSummary.TotalAppointments > 0 ? (schedulingSummary.RescheduledAppointments / schedulingSummary.TotalAppointments) * 100 : 0;
                let d5 = {
                    y: schedulingSummary.RescheduledAppointments,
                    name: Math.round(rescheduledPercentage),
                };
                rescheduledList.push(d5);
                yAxisValues.push({ name: 'Rescheduled', data: rescheduledList, color: 'purple', showInLegend: true });
            }

            if (
                !Boolean(schedulingSummary.ConfirmedAppointments) &&
                !Boolean(schedulingSummary.CancelledAppointments) &&
                !Boolean(schedulingSummary.NoCallOrNoShowAppointments) &&
                !Boolean(schedulingSummary.CompletedAppointments) &&
                !Boolean(schedulingSummary.UnconfirmedAppointments) &&
                !Boolean(schedulingSummary.RescheduledAppointments)
            ) {
                setChartOptions(
                    configureStackedBarChart(
                        'Team Appointment Hub',
                        'Appointments Count',
                        'Appointments',
                        'Count',
                        [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                        [0],
                        'Scheduling',
                        history
                    )
                );
            } else {
                let subTitle = '',
                    xAxisTitle = '',
                    yAxisTitle = 'Count';
                setChartOptions(
                    configureStackedBarChart('Appointment Chart', subTitle, xAxisTitle, yAxisTitle, yAxisValues, xAxisValues, 'Scheduling', history)
                );
            }

            setIsGraphShow(true);
        }
    }, [schedulingSummary, history]);

    const changeFromDate = (date: any) => {
        setSchedulingFromDate(date);
    };

    const changeToDate = (date: any) => {
        setSchedulingToDate(date);
    };

    const navigateToMembers = () => {
        history(`/provider/metricsummary/teamappointmenthubdetail?hasElevatedFilterAccess=${props.hasElevatedFilterAccess}`);
    };

    const toggleProviderHandler = (isChecked: boolean) => {
        setSchedulingShowInActiveProviders(isChecked);
        setSchedulingSelectedProvider('All');
    };

    const toggleStaffHandler = (isChecked: boolean) => {
        setSchedulingShowInActiveStaffs(isChecked);
        setSchedulingSelectedStaff('All');
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={1.9} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Team Appointment Hub
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Providers :
                                            </Typography>
                                            <Typography variant="body2">Your performance is measured on percent of members seen in person.</Typography>
                                            <Typography variant="caption">
                                                Number of Follow-Up, Behavioral Health, Integrated, Rescheduled, Initial, Telemedicine, Transitional Care, OnBoard and Facility Visit appointments completed
                                                and documented on divided by the total number of Follow-Up, Behavioral Health, Integrated, Rescheduled, Initial,
                                                Telemedicine, Transitional Care, OnBoard and Facility Visit appointments of scheduled presented as a percentage to display what percent of your in person
                                                appointments were completed.
                                            </Typography>
                                            <Typography marginTop={1} fontWeight={800} variant="subtitle2">
                                                Schedulers :
                                            </Typography>
                                            <Typography variant="body2">
                                                Your performance is measured on percent of members seen in person by your POD’s providers.
                                            </Typography>
                                            <Typography variant="caption">
                                                Drill down to take action – Click into Cancelled, NCNS (No-Call/No-Show), and Unconfirmed appointments to follow
                                                up. This widget shows how well you’re confirming and rescheduling appointments. Switch between team-wide or
                                                provider-specific views to manage priorities.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={4}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={schedulingSelectedTeam}
                                    onChange={(id) => setSchedulingSelectedTeam(id)}
                                    showInactive={schedulingShowInActiveTeams}
                                    onToggleInactive={setSchedulingShowInActiveTeams}
                                />
                            </Grid>)}
                        <Grid size={4}>
                            <HealthPlanMultiSelect
                                value={schedulingSelectedPlans}
                                onChange={setSchedulingSelectedPlans}
                                menuProps={MenuProps}
                            />
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Providers</InputLabel>
                                <Select
                                    value={schedulingSelectedProvider}
                                    label="Providers"
                                    name="Providers"
                                    onChange={(e) => setSchedulingSelectedProvider(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={
                                            <Checkbox checked={schedulingShowInActiveProviders} onChange={(e) => toggleProviderHandler(e.target.checked)} />
                                        }
                                        label="Show Inactive"
                                    />
                                    {filteredProviders.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Scheduling Staff</InputLabel>
                                <Select
                                    value={schedulingSelectedStaff}
                                    label="Scheduling Staff"
                                    name="Scheduling Staff"
                                    onChange={(e) => setSchedulingSelectedStaff(e.target.value)}
                                    MenuProps={MenuProps}
                                >
                                    <MenuItem key="All" value="All">
                                        All
                                    </MenuItem>
                                    <FormControlLabel
                                        sx={{ pl: '12px', mb: '0px' }}
                                        control={<Checkbox checked={schedulingShowInActiveStaffs} onChange={(e) => toggleStaffHandler(e.target.checked)} />}
                                        label="Show Inactive"
                                    />
                                    {filteredStaffs.map((pro) => (
                                        <MenuItem key={pro.Id} value={pro.Id} sx={{ color: pro.Role.RoleName === 'Inactive' ? 'grey' : 'black' }}>
                                            {pro.LastName}, {pro.FirstName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={4}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={schedulingDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setSchedulingDateFilter(parseInt(e.target.value as any))}
                                >
                                    {filterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? (
                            <React.Fragment>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={Boolean(schedulingFromDate) ? schedulingFromDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeFromDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={{ md: 4 }}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={Boolean(schedulingToDate) ? schedulingToDate : null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={(newValue) => changeToDate(newValue)}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                            </React.Fragment>
                        ) : null}
                        <Grid size={12} sx={{ position: 'relative', paddingTop: schedulingDateFilter === AppointmentTimeFrameEnum.Custom ? '0 !important' : '50px !important', }}>
                            {!isGraphShow ? (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            ) : null}
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default TeamAppointmentsHub;
